/**
 * Pure helpers for the SleepOverlayScene action wiring.
 *
 * Extracted into a Phaser-free module so the unit tests can import these
 * without pulling in the full Phaser runtime (which needs a browser
 * `window`).
 *
 * SMA-52 — the bedtime ritual now lets the player set a dream intent
 * (Dream-Weaving §1.1 — "What shall the dream be about?") before they
 * commit to the night. The intent is submitted to the server via
 * {@link MSG_DREAM_WEAVE}; the next Dreaming-state Whisper accrual reads
 * it and biases the picker (server seam B-038). When the player skips the
 * intent the prompt is silently dropped — `dream-weaving.md` §3.3 calls
 * the no-intent path "private," and the bias picker falls through to the
 * unweighted draw.
 */

import {
  COMBAT_PROTOCOL_VERSION,
  MSG_DREAM_WEAVE,
  type DreamWeaveRequest,
} from '@game/shared';

/**
 * Stable message id for the "stay until dawn" intent.
 *
 * The client uses this both as a hint for the server (when a future
 * server-side time-compression handler lands) and as a marker for QA
 * tooling that wants to assert the verb fired without spinning up a real
 * Colyseus connection.
 */
export const MSG_SLEEP_COMPRESS_TO_DAWN = 'sleep_compress_to_dawn';

/** Minimal contract the overlay needs from its parent world scene. */
export interface SleepOverlayDeps {
  /** Send a typed Colyseus message. May be null if the room is gone. */
  readonly sendMessage: (type: string, payload: Record<string, unknown>) => void;
  /** Close the underlying Colyseus session. Server flips Dreaming on disconnect. */
  readonly leaveRoom: () => void;
  /** Resume / shutdown the overlay scene without firing either action. */
  readonly dismiss: () => void;
  /** Optional: a night actually passed. Advances the traveler's day count
   *  (C5 hanko stamp). Not called on `dismiss` — backing out of the bedtime
   *  ritual must not cost a day. */
  readonly onNightPassed?: () => void;
}

export interface SleepActions {
  /** Stay logged in; ask the server to compress to dawn. */
  readonly stayUntilDawn: () => void;
  /** Disconnect; server's `onLeave` flips us to Dreaming. */
  readonly sleepUntilReturn: () => void;
  /** Resume the world without sleeping. */
  readonly dismiss: () => void;
  /** Submit a free-text dream intent (Dream-Weaving §1.1). Empty/whitespace
   *  is treated as a clear (server-side semantics). Trims trailing spaces;
   *  leaves clamping (≤140 chars) to the server. */
  readonly submitIntent: (text: string) => void;
}

/**
 * Build the four action callbacks the overlay's UI invokes. Pure — no
 * Phaser, no globals, no side effects beyond the deps it is handed.
 */
export function buildSleepActions(deps: SleepOverlayDeps): SleepActions {
  return {
    stayUntilDawn: () => {
      deps.sendMessage(MSG_SLEEP_COMPRESS_TO_DAWN, {});
      deps.onNightPassed?.();
      deps.dismiss();
    },
    sleepUntilReturn: () => {
      deps.onNightPassed?.();
      deps.leaveRoom();
    },
    dismiss: () => {
      deps.dismiss();
    },
    submitIntent: (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      const payload: DreamWeaveRequest = {
        v: COMBAT_PROTOCOL_VERSION,
        intent: trimmed,
      };
      deps.sendMessage(MSG_DREAM_WEAVE, payload as unknown as Record<string, unknown>);
    },
  };
}
